import { LoadMore } from "../components/ConversationTools";
import { onUserEvent, coalesce, useUserSocketHold } from "../lib/userEvents";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Search, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { smallImage, thumbUrl } from "../lib/images";
import { useI18n } from "../i18n";
import { WorkflowTemplates } from "../components/WorkflowTemplates";
import { continueInChat } from "../components/ProjectionPanel";

const STATUSES = ["all", "draft", "running", "waiting_approval", "paused", "completed", "failed"];
const STATUS_LABEL = { all: "Semua", draft: "Draft", running: "Berjalan", queued: "Menunggu", planning: "Perencanaan", waiting_approval: "Perlu persetujuan", paused: "Dijeda", completed: "Selesai", failed: "Gagal", cancelled: "Batal", scheduled: "Terjadwal" };
const statusColor = { completed: "#10B981", running: "#8B5CF6", queued: "#F59E0B", planning: "#2F6BFF", failed: "#EF4444", cancelled: "#94A3B8", draft: "#64748B", waiting_approval: "#F59E0B", paused: "#F97316", scheduled: "#2F6BFF" };

export default function Workspace() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [goal, setGoal] = useState("");
  const [tasks, setTasks] = useState([]);
  useUserSocketHold(tasks.some((t) => ["queued", "running"].includes(t.status))); // live progress needs the socket; released when nothing runs
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const PAGE = 20;
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const load = (append = false) => {
    if (append) setLoadingMore(true);
    const params = [`limit=${PAGE}`, `offset=${append ? tasks.length : 0}`];
    if (filter !== "all") params.push(`status=${filter}`);
    if (q) params.push(`q=${encodeURIComponent(q)}`);
    api.get(`/tasks?${params.join("&")}`).then((r) => { setTasks((prev) => (append ? [...prev, ...r.data.filter((t) => !prev.some((p) => p.id === t.id))] : r.data)); setMore(r.data.length === PAGE); }).catch(() => {}).finally(() => setLoadingMore(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, q]);
  const patchRow = (taskId) => api.get(`/tasks/${taskId}/summary`).then((r) => {  // one row refreshed in place; a new task matching the filter is prepended
    const row = r.data;
    setTasks((prev) => {
      if (prev.some((t) => t.id === row.id)) return prev.map((t) => (t.id === row.id ? row : t));
      if (filter !== "all" && row.status !== filter) return prev;
      if (q && !`${row.title} ${row.goal}`.toLowerCase().includes(q.toLowerCase())) return prev;
      return [row, ...prev];
    });
  }).catch((e) => { if (e?.response?.status === 404) setTasks((prev) => prev.filter((t) => t.id !== taskId)); });
  useEffect(() => {
    const off = onUserEvent(["task_update", "ws_state"], (e) => { if (e.type === "task_update" && e.task_id) coalesce(`ws-row-${e.task_id}`, () => patchRow(e.task_id)); else if (e.type === "ws_state" && e.connected) coalesce("workspace-list", load); });
    return off; /* eslint-disable-next-line */
  }, [filter, q]);

  const [story, setStory] = useState("");
  const [starting, setStarting] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const startTask = async () => { // «Tugas baru»: the request becomes a draft task + its task room (stage 1 «Penggalian kebutuhan» running)
    if (!story.trim()) return;
    setStarting(true);
    try { const r = await api.post("/tasks/from-text", { text: story.trim() }); setStory(""); nav(`/chat/${r.data.room_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tugas"); } finally { setStarting(false); }
  };
  const delegate = async () => {
    if (!goal.trim()) return;
    setBusy(true);
    try { const r = await api.post("/tasks", { goal }); setGoal(""); toast.success("Workflow disusun — periksa, sesuaikan, lalu klik Run"); nav(`/workspace/${r.data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyusun workflow"); } finally { setBusy(false); }
  };
  const del = async (id, e) => { e.stopPropagation(); await api.delete(`/tasks/${id}`); load(); };

  return (
    <div className="mx-auto max-w-5xl p-5 sm:p-8 lg:p-10 fade-up" data-testid="workspace-page">
      <h1 className="text-3xl font-extrabold text-slate-900">{t("nav.workspace")}</h1>
      <p className="text-sm text-slate-500">Beri Oryntix sebuah tujuan — AI menyusun workflow langkah demi langkah, Anda tinjau & jalankan.</p>

      <div className="mt-6 aivora-card p-5" style={{ background: "linear-gradient(135deg, rgba(0,209,255,.08), rgba(124,58,237,.1))" }} data-testid="new-task-box">
        <p className="mb-2 text-sm font-semibold text-slate-800">Tugas baru</p>
        <textarea className="input-dark min-h-[110px]" placeholder="Ceritakan tugasmu — asistenmu akan menggali kebutuhannya di room tugas, lalu kalian susun bersama." value={story} onChange={(e) => setStory(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) startTask(); }} data-testid="task-story-input" />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button onClick={startTask} disabled={starting || !story.trim()} className="btn-grad flex items-center gap-2 rounded-xl px-6 py-3 text-sm disabled:opacity-60" data-testid="task-story-start">
            {starting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {starting ? "Membuka room tugas…" : "Mulai di room tugas"}
          </button>
          <span className="text-xs text-slate-500" data-testid="task-story-hint">Tahap 1 <b>Penggalian kebutuhan</b> dimulai begitu room terbuka; tahapan berikutnya diusulkan asisten dan kamu setujui.</span>
          <button type="button" onClick={() => setAdvanced((v) => !v)} className="ml-auto text-xs font-semibold text-[#2F6BFF] hover:underline" data-testid="advanced-mode-toggle">{advanced ? "Tutup mode lanjutan" : "Mode lanjutan"}</button>
        </div>
        {advanced && (
          <div className="mt-4 rounded-xl border border-slate-200/70 bg-white/60 p-4" data-testid="advanced-mode">
            <p className="mb-2 text-xs text-slate-500">Mode lanjutan: AI menyusun workflow dari tujuanmu — kamu atur langkah dan agennya sendiri sebelum dijalankan.</p>
            <textarea className="input-dark min-h-[90px]" placeholder={t("work.delegate")} value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="goal-input" />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button onClick={delegate} disabled={busy || !goal.trim()} className="btn-grad flex items-center gap-2 rounded-xl px-6 py-3 text-sm disabled:opacity-60" data-testid="delegate-btn">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {busy ? "Menyusun workflow..." : "Susun workflow"}
              </button>
              <span className="text-xs text-slate-500" data-testid="delegate-hint">Setelah ini Anda memilih tipe tugas: <b>Segera</b> atau <b>24 Jam (batch, hemat 50%)</b>, dan mengatur langkahnya.</span>
            </div>
            <WorkflowTemplates goal={goal} />
          </div>
        )}
      </div>


      <div className="mt-8 flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`rounded-full px-4 py-1.5 text-xs font-medium ${filter === s ? "btn-grad" : "border border-slate-200 text-slate-500"}`}>{STATUS_LABEL[s] || s}</button>
        ))}
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className="input-dark w-48 py-2 pl-9" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="task-search" />
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {tasks.length === 0 ? <p className="py-10 text-center text-slate-500">Belum ada tugas.</p> : tasks.map((tk) => (
          <div key={tk.id} onClick={() => nav(`/workspace/${tk.id}`)} data-testid={`task-${tk.id}`}
            className="aivora-card aivora-card-hover group flex cursor-pointer items-center justify-between p-5">
            <div className="flex min-w-0 items-center gap-3">
              {tk.thumbnail?.path || tk.thumbnail?.url ? <img src={tk.thumbnail.url || thumbUrl(tk.thumbnail.path)} alt="" loading="lazy" decoding="async" className="h-12 w-12 shrink-0 rounded-lg object-cover" data-testid={`task-thumb-${tk.id}`} />
                : tk.portrait ? <img src={smallImage(tk.portrait)} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-lg object-cover" /> : null}
              <div className="min-w-0">
                <p className="truncate font-semibold text-slate-900" data-no-i18n>{tk.title || tk.goal}</p>
                <p className="mt-1 text-xs text-slate-500">{(tk.steps_total ?? 0) > 0 ? `${tk.steps_done ?? 0}/${tk.steps_total} ${tk.type === "workflow" ? "langkah" : "subtugas"} · ` : ""}{Number(tk.credits_used || 0).toFixed(1)} kredit</p>
              </div>
            </div>
            <div className="ml-4 flex items-center gap-3">
              {tk.method === "batch" && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700" title="Metode batch · hemat 50%" data-testid={`task-batch-badge-${tk.id}`}>Batch{tk.status === "running" ? " · menunggu" : ""}</span>}
              {tk.source === "chat_projection" && tk.conversation_id && <button onClick={(e) => { e.stopPropagation(); continueInChat(nav, tk); }} className="rounded-full border border-[#2F6BFF]/30 px-3 py-1 text-xs font-semibold text-[#2F6BFF] hover:bg-[#EEF3FF]" data-testid={`task-continue-chat-${tk.id}`}>Lanjutkan di chat</button>}
              <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: `${statusColor[tk.status] || "#94A3B8"}22`, color: statusColor[tk.status] || "#94A3B8" }}>{STATUS_LABEL[tk.status] || tk.status}</span>
              <button onClick={(e) => del(tk.id, e)} className="text-slate-500 opacity-0 transition group-hover:opacity-100 hover:text-[#EF4444]"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {more && <LoadMore onClick={() => !loadingMore && load(true)} loading={loadingMore} testid="tasks-load-more" />}
      </div>
    </div>
  );
}
